import React, { useEffect, useState } from "react";
import { Button, CircularProgress, Dialog, IconButton, Tooltip } from "@mui/material";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";
import RefreshRoundedIcon from "@mui/icons-material/RefreshRounded";
import Flx from "../layout/Flx";
import Txt from "../typography/Txt";
import colors from "../../theme/colors";
// LENDMARQ DIVERGENCE: hook bridge. The links hook is the host app's
// (src/hooks), not a vendored copy — one extra ".." out of src/tpo.
import { useApplicationLinksHook } from "../../../hooks/useApplicationLinksHook";
import GeneratedLinksPanel from "../../screens/SendToBorrowerScreen/components/GeneratedLinksPanel";
import {
  configsForSelection,
  LINK_SELECTION_BOTH,
} from "../../screens/SendToBorrowerScreen/sendToBorrowerConfig";
import { mintShareableLinks } from "../../screens/SendToBorrowerScreen/shareableLinks";

/**
 * The broker's own dedicated (evergreen) links — loan application AND credit
 * authorization — straight into a modal, skipping the send screen's options.
 *
 * Same result as choosing "Both" + "Get a shareable link" on /send: the same
 * `mintShareableLinks` call and the same GeneratedLinksPanel, so the card the
 * broker copies from here is the one they would have reached there. The links
 * are get-or-create per (owner, kind), so opening this repeatedly is a read,
 * not a mint — which is why it fetches on open with no confirmation step.
 *
 * Chrome follows SendDocumentModal: the dialog surface is the card, so the
 * panel renders frameless inside it.
 */
const ShareableLinksModal = ({ open, onClose }) => {
  const { createDedicatedLink } = useApplicationLinksHook();
  const [results, setResults] = useState(null);
  const [failed, setFailed] = useState(false);
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setResults(null);
    setFailed(false);

    mintShareableLinks({
      createDedicatedLink,
      configs: configsForSelection(LINK_SELECTION_BOTH),
    }).then((minted) => {
      if (cancelled) return;
      // All or nothing, as on the send screen: one link missing from a pair
      // would read as a finished result rather than a failure.
      if (minted.some((result) => !result)) {
        setFailed(true);
        return;
      }
      setResults(minted);
    });

    return () => {
      cancelled = true;
    };
    // `createDedicatedLink` is re-created on every render of the hook; keying on
    // it would refetch in a loop. `attempt` is the explicit retry.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, attempt]);

  if (!open) return null;

  return (
    <Dialog
      open
      onClose={onClose}
      maxWidth={false}
      PaperProps={{
        sx: {
          // Narrower than the send screen's 1080: this is the result card
          // alone, and the link rows wrap comfortably at this width.
          width: "min(820px, calc(100vw - 4rem))",
          maxWidth: "min(820px, calc(100vw - 4rem))",
          maxHeight: "calc(100vh - 4rem)",
          m: 0,
          borderRadius: "12px",
          backgroundColor: colors.white,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        },
      }}
    >
      <Flx
        centerY
        gap={1.5}
        sx={{
          flexShrink: 0,
          px: 2.5,
          py: 1.75,
          borderBottom: `1px solid ${colors.slate200}`,
        }}
      >
        <Txt
          component="div"
          sx={{ fontSize: 15.5, fontWeight: 700, minWidth: 0, flex: 1 }}
        >
          Shareable links
        </Txt>
        <Tooltip title="Close">
          <IconButton size="small" onClick={onClose} sx={{ flexShrink: 0 }}>
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Flx>

      <Flx column sx={{ flex: 1, minHeight: 0, overflowY: "auto", p: 3 }}>
        {results ? (
          <GeneratedLinksPanel results={results} onDone={onClose} frameless />
        ) : failed ? (
          <Flx column centerX gap={1.5} sx={{ py: 4, textAlign: "center" }}>
            <Txt component="div" sx={{ fontSize: 14, color: colors.slate600 }}>
              Could not get your shareable links.
            </Txt>
            <Button
              variant="outlined"
              size="small"
              startIcon={<RefreshRoundedIcon sx={{ fontSize: 16 }} />}
              onClick={() => setAttempt((n) => n + 1)}
            >
              Try again
            </Button>
          </Flx>
        ) : (
          <Flx centerX centerY gap={1.5} sx={{ py: 6 }}>
            <CircularProgress size={20} />
            <Txt component="div" sx={{ fontSize: 13.5, color: colors.slate500 }}>
              Getting your links...
            </Txt>
          </Flx>
        )}
      </Flx>
    </Dialog>
  );
};

export default ShareableLinksModal;
